import type { Client, IMessage, StompSubscription } from "@stomp/stompjs";

export type MessageHandler = (msg: IMessage) => void | Promise<void>;

type TopicState = {
    sub: StompSubscription;
    handlers: Map<string, MessageHandler>; // key -> handler
};

const topics = new Map<string, TopicState>();

function genId() {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Ensure single STOMP subscribe per topic; allow multiple handlers.
 * Handlers are called sequentially in insertion order.
 */
export function registerTopicHandler(opts: {
    client: Client;
    topic: string;
    handler: MessageHandler;
    clientId?: string;
}): { id: string; unsubscribe: () => void } {
    const { client, topic, handler, clientId } = opts;

    let state = topics.get(topic);

    // Create STOMP subscription once
    if (!state) {
        const handlers = new Map<string, MessageHandler>();

        const sub = client.subscribe(topic, async (msg: IMessage) => {
            // Call sequentially (await each)
            console.log(topic, handlers);
            for (const [, h] of handlers) {
                try {
                    await h(msg);
                } catch (e) {
                    console.error(`[WS] handler error topic=${topic}`, e);
                }
            }
        });

        state = { sub, handlers };
        topics.set(topic, state);
    }

    const id = clientId ?? genId();
    state.handlers.set(id, handler);

    const unsubscribe = () => {
        const s = topics.get(topic);
        if (!s) return;

        s.handlers.delete(id);

        // If no handlers left, unsubscribe from server
        if (s.handlers.size === 0) {
            try {
                s.sub.unsubscribe();
            } catch (e) {
                console.warn(`[WS] unsubscribe error topic=${topic}`, e);
            }
            topics.delete(topic);
        }
    };

    return { id, unsubscribe };
}

/**
 * Optional: call on disconnect to clear local registry.
 * (You usually don't need this if client is recreated on reconnect.)
 */
export function clearAllTopicHandlers() {
    for (const [topic, state] of topics) {
        try {
            state.sub.unsubscribe();
        } catch { }
        topics.delete(topic);
    }
}